import type { ChangeEvent, ReactNode } from 'react'
import { Fragment, useEffect, useId, useRef } from 'react'

/**
 * Componenti di base dell'interfaccia.
 *
 * Sono l'unico punto in cui si scrivono le classi di presentazione dei controlli: le pagine
 * compongono questi elementi e non applicano stili propri. Il vincolo esiste perche' prima
 * ogni pagina dichiarava i propri, e le differenze si accumulavano senza che nessuno le
 * vedesse: 109 attributi `style` in linea, sette pesi tipografici, ventiquattro dimensioni
 * di testo.
 *
 * Nessuno di questi componenti accetta `style` o `className` liberi. Le varianti sono
 * enumerate: se serve un aspetto nuovo si aggiunge qui una variante, non un'eccezione
 * nella pagina.
 */

// --- comandi ---------------------------------------------------------------------------------

type VarianteBottone = 'primario' | 'secondario' | 'discreto' | 'critico'

export function Bottone({
  variante = 'secondario', dimensione = 'normale', icona, inCorso = false,
  larghezzaPiena = false, tipo = 'button', children, ...resto
}: {
  variante?: VarianteBottone
  dimensione?: 'normale' | 'piccolo'
  /** Icona a sinistra dell'etichetta. Mai da sola senza `titolo`. */
  icona?: ReactNode
  /** Durante un'operazione il comando resta visibile ma non ripetibile. */
  inCorso?: boolean
  larghezzaPiena?: boolean
  tipo?: 'button' | 'submit'
  children?: ReactNode
  disabled?: boolean
  title?: string
  onClick?: () => void
  'aria-label'?: string
  /** Nota che descrive il comando: per esempio l'errore del campo che il comando compila. */
  'aria-describedby'?: string
  /** Per il comando che apre e chiude un pannello: stato e pannello governato. */
  'aria-expanded'?: boolean
  'aria-controls'?: string
  /** Per il comando a due stati (la palestra, 490): acceso o spento. */
  'aria-pressed'?: boolean
}) {
  const classi = ['bottone', `bottone-${variante}`]
  if (dimensione === 'piccolo') classi.push('bottone-piccolo')
  if (larghezzaPiena) classi.push('bottone-pieno')
  // `disabled` va calcolato DOPO lo spread: scritto prima veniva riscritto dal valore del
  // chiamante, e con `inCorso` e `disabled` passati insieme il comando restava premibile
  // mentre l'operazione era in corso, quindi ripetibile.
  return (
    <button type={tipo} className={classi.join(' ')} {...resto}
            disabled={resto.disabled || inCorso}>
      {inCorso ? <span className="rotella" aria-hidden="true" /> : icona}
      {children}
    </button>
  )
}

/** Comando di sola icona. Richiede un'etichetta testuale: senza, non e' annunciato. */
export function BottoneIcona({
  icona, etichetta, variante = 'discreto', ...resto
}: {
  icona: ReactNode
  etichetta: string
  variante?: VarianteBottone
  disabled?: boolean
  onClick?: () => void
  /** Per il comando che apre e chiude un pannello (il menu a scomparsa): stato e pannello governato. */
  'aria-expanded'?: boolean
  'aria-controls'?: string
}) {
  return (
    <button type="button" className={`bottone bottone-${variante} bottone-icona`}
            title={etichetta} aria-label={etichetta} {...resto}>
      {icona}
    </button>
  )
}

// --- campi ------------------------------------------------------------------------------------

type ProprietaComuni = {
  etichetta: string
  aiuto?: ReactNode
  errore?: string | null
  obbligatorio?: boolean
  disabilitato?: boolean
}

function Involucro({
  id, etichetta, aiuto, errore, obbligatorio, children,
}: ProprietaComuni & { id: string; children: ReactNode }) {
  return (
    <div className={`campo${errore ? ' campo-errato' : ''}`}>
      <label htmlFor={id}>
        {etichetta}
        {obbligatorio && <span className="campo-obbligatorio" aria-hidden="true"> *</span>}
      </label>
      {children}
      {errore
        ? <p className="campo-errore" id={`${id}-nota`} role="alert">{errore}</p>
        : aiuto ? <p className="campo-aiuto" id={`${id}-nota`}>{aiuto}</p> : null}
    </div>
  )
}

export function Campo({
  tipo = 'text', valore, iniziale, cambia, suFuoco, segnaposto, autoComplete, minLength, maxLength,
  min, max, passi, riferimento, modalitaTastiera, ...comuni
}: ProprietaComuni & {
  /** `time` per l'ora d'inizio delle mediazioni: il browser propone il selettore dell'ora. */
  tipo?: 'text' | 'email' | 'password' | 'date' | 'time' | 'datetime-local' | 'number' | 'tel' | 'search'
  /**
   * Senza `valore` il campo non e' governato da React: parte da `iniziale` e il contenuto lo scrive
   * chi tiene il `riferimento`. Serve ai campi scritti da piu' persone insieme (bozze condivise, 505),
   * dove le modifiche altrui entrano nel campo senza spostare il cursore di chi scrive.
   */
  valore?: string | number
  iniziale?: string
  cambia: (v: string) => void
  /** Il campo prende il fuoco: la bozza condivisa dice agli altri dove si sta scrivendo. */
  suFuoco?: () => void
  /**
   * Tastiera da proporre sui dispositivi tattili: `numeric` per i codici a cifre, `decimal` dove
   * serve il separatore (per esempio le ore di mediazione, a multipli di mezz'ora).
   */
  modalitaTastiera?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email'
  segnaposto?: string
  autoComplete?: string
  minLength?: number
  maxLength?: number
  min?: number | string
  max?: number | string
  passi?: number
  riferimento?: React.Ref<HTMLInputElement>
}) {
  const id = useId()
  return (
    <Involucro id={id} {...comuni}>
      <input
        id={id} type={tipo} value={valore} defaultValue={valore === undefined ? iniziale : undefined}
        ref={riferimento} onFocus={suFuoco}
        onChange={(e: ChangeEvent<HTMLInputElement>) => cambia(e.target.value)}
        placeholder={segnaposto} autoComplete={autoComplete} inputMode={modalitaTastiera}
        minLength={minLength} maxLength={maxLength} min={min} max={max} step={passi}
        required={comuni.obbligatorio} disabled={comuni.disabilitato}
        aria-invalid={comuni.errore ? true : undefined}
        aria-describedby={comuni.errore || comuni.aiuto ? `${id}-nota` : undefined}
      />
    </Involucro>
  )
}

export function AreaTesto({
  valore, iniziale, cambia, suFuoco, righe = 3, segnaposto, maxLength, riferimento, ...comuni
}: ProprietaComuni & {
  /** Senza `valore`, come in Campo: parte da `iniziale` e lo scrive chi tiene il `riferimento`. */
  valore?: string
  iniziale?: string
  cambia: (v: string) => void
  suFuoco?: () => void
  righe?: number
  segnaposto?: string
  maxLength?: number
  riferimento?: React.Ref<HTMLTextAreaElement>
}) {
  const id = useId()
  return (
    <Involucro id={id} {...comuni}>
      <textarea
        id={id} value={valore} defaultValue={valore === undefined ? iniziale : undefined} rows={righe}
        placeholder={segnaposto} maxLength={maxLength} ref={riferimento} onFocus={suFuoco}
        onChange={(e) => cambia(e.target.value)}
        required={comuni.obbligatorio} disabled={comuni.disabilitato}
        aria-invalid={comuni.errore ? true : undefined}
        aria-describedby={comuni.errore || comuni.aiuto ? `${id}-nota` : undefined}
      />
    </Involucro>
  )
}

/** `gruppo`: le voci consecutive con lo stesso gruppo stanno sotto un titolo (optgroup). */
export type Opzione = { valore: string; etichetta: string; gruppo?: string }

/** Le voci divise in gruppi consecutivi; senza gruppi, un gruppo solo senza titolo. */
function inGruppi(opzioni: Opzione[]): { titolo?: string; voci: Opzione[] }[] {
  const gruppi: { titolo?: string; voci: Opzione[] }[] = []
  for (const o of opzioni) {
    const ultimo = gruppi.at(-1)
    if (ultimo && ultimo.titolo === o.gruppo) ultimo.voci.push(o)
    else gruppi.push({ titolo: o.gruppo, voci: [o] })
  }
  return gruppi
}

export function Scelta({
  valore, cambia, opzioni, vuota, ...comuni
}: ProprietaComuni & {
  valore: string
  cambia: (v: string) => void
  opzioni: Opzione[]
  /** Prima voce neutra. Assente se la scelta e' obbligatoria e ha un valore iniziale. */
  vuota?: string
}) {
  const id = useId()
  return (
    <Involucro id={id} {...comuni}>
      <select
        id={id} value={valore} onChange={(e) => cambia(e.target.value)}
        required={comuni.obbligatorio} disabled={comuni.disabilitato}
        aria-invalid={comuni.errore ? true : undefined}
        aria-describedby={comuni.errore || comuni.aiuto ? `${id}-nota` : undefined}
      >
        {vuota !== undefined && <option value="">{vuota}</option>}
        {inGruppi(opzioni).map((g, i) => {
          const voci = g.voci.map((o) => <option key={o.valore} value={o.valore}>{o.etichetta}</option>)
          return g.titolo
            ? <optgroup key={`${i}-${g.titolo}`} label={g.titolo}>{voci}</optgroup>
            : <Fragment key={i}>{voci}</Fragment>
        })}
      </select>
    </Involucro>
  )
}

export function Spunta({
  etichetta, valore, cambia, aiuto, disabilitato,
}: {
  etichetta: ReactNode
  valore: boolean
  cambia: (v: boolean) => void
  aiuto?: ReactNode
  disabilitato?: boolean
}) {
  const id = useId()
  return (
    <div className="spunta">
      <input id={id} type="checkbox" checked={valore} disabled={disabilitato}
             onChange={(e) => cambia(e.target.checked)}
             aria-describedby={aiuto ? `${id}-nota` : undefined} />
      <label htmlFor={id}>{etichetta}</label>
      {aiuto && <p className="campo-aiuto" id={`${id}-nota`}>{aiuto}</p>}
    </div>
  )
}

// --- superfici --------------------------------------------------------------------------------

export function Riquadro({
  titolo, azioni, children, denso = false,
}: {
  titolo?: ReactNode
  azioni?: ReactNode
  children: ReactNode
  /** Riduce l'imbottitura: per i riquadri che contengono una tabella a tutta larghezza. */
  denso?: boolean
}) {
  return (
    <section className={`riquadro${denso ? ' riquadro-denso' : ''}`}>
      {(titolo || azioni) && (
        <header className="riquadro-capo">
          {titolo && <h2>{titolo}</h2>}
          {azioni && <div className="riquadro-azioni">{azioni}</div>}
        </header>
      )}
      <div className="riquadro-corpo">{children}</div>
    </section>
  )
}

export function Tabella({
  intestazioni, children, vuoto, larghezze,
}: {
  intestazioni: ReactNode[]
  children: ReactNode
  /** Mostrato al posto della tabella quando non ci sono righe. */
  vuoto?: ReactNode
  larghezze?: (string | undefined)[]
}) {
  const righe = Array.isArray(children) ? children.flat().filter(Boolean) : children
  const nessuna = Array.isArray(righe) && righe.length === 0
  if (nessuna && vuoto) return <div className="tabella-vuota">{vuoto}</div>
  return (
    <div className="tabella-scorrevole">
      <table className="tabella">
        <thead>
          <tr>{intestazioni.map((t, i) => (
            <th key={i} style={larghezze?.[i] ? { width: larghezze[i] } : undefined}>{t}</th>
          ))}</tr>
        </thead>
        <tbody>{righe}</tbody>
      </table>
    </div>
  )
}

// --- sovrapposizioni --------------------------------------------------------------------------

/** Finestre aperte, l'ultima in coda: tastiera e fuoco li governa solo quella in primo piano. */
const finestreAperte: HTMLElement[] = []

const ATTIVABILI = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Elementi raggiungibili con il tabulatore dentro `radice`, esclusi quelli non visualizzati. */
function attivabili(radice: HTMLElement): HTMLElement[] {
  return [...radice.querySelectorAll<HTMLElement>(ATTIVABILI)]
    .filter((el) => !el.hidden && el.getClientRects().length > 0)
}

/**
 * Finestra modale.
 *
 * Il fuoco entra nella finestra all'apertura, sul primo elemento attivabile del corpo (o dei comandi
 * con `fuocoIniziale="azioni"`), altrimenti sulla finestra stessa; resta dentro con Tab e
 * Maiuscolo+Tab; torna all'elemento che l'aveva aperta alla chiusura. Esc chiude ovunque sia il fuoco.
 * Prima nessuna di queste cose accadeva: aperta da un comando, la finestra lasciava il fuoco sul
 * comando sotto il velo, il tabulatore scorreva la pagina coperta ed Esc non chiudeva.
 */
export function Modale({
  titolo, chiudi, azioni, larga = false, fuocoIniziale = 'corpo', children,
}: {
  titolo: string
  chiudi: () => void
  azioni?: ReactNode
  larga?: boolean
  /** Dove va il fuoco all'apertura: il primo campo del corpo, oppure il primo comando. */
  fuocoIniziale?: 'corpo' | 'azioni'
  children: ReactNode
}) {
  const finestra = useRef<HTMLDivElement>(null)
  const idTitolo = useId()
  // La funzione di chiusura cambia a ogni render del chiamante: l'ascoltatore la legge da qui.
  const chiudiAttuale = useRef(chiudi)
  chiudiAttuale.current = chiudi

  useEffect(() => {
    const el = finestra.current
    if (!el) return
    const precedente = document.activeElement instanceof HTMLElement ? document.activeElement : null
    finestreAperte.push(el)
    const zona = el.querySelector<HTMLElement>(fuocoIniziale === 'azioni' ? '.modale-azioni' : '.modale-corpo')
    ;((zona && attivabili(zona)[0]) || el).focus()

    const tasto = (e: KeyboardEvent) => {
      if (finestreAperte[finestreAperte.length - 1] !== el) return
      if (e.key === 'Escape') {
        e.preventDefault()
        chiudiAttuale.current()
        return
      }
      if (e.key !== 'Tab') return
      const elementi = attivabili(el)
      if (!elementi.length) { e.preventDefault(); el.focus(); return }
      const primo = elementi[0]
      const ultimo = elementi[elementi.length - 1]
      const attivo = document.activeElement
      if (!el.contains(attivo)) {
        e.preventDefault()
        ;(e.shiftKey ? ultimo : primo).focus()
      } else if (e.shiftKey && (attivo === primo || attivo === el)) {
        e.preventDefault()
        ultimo.focus()
      } else if (!e.shiftKey && attivo === ultimo) {
        e.preventDefault()
        primo.focus()
      }
    }
    document.addEventListener('keydown', tasto)
    return () => {
      document.removeEventListener('keydown', tasto)
      const posizione = finestreAperte.indexOf(el)
      if (posizione >= 0) finestreAperte.splice(posizione, 1)
      if (precedente?.isConnected) precedente.focus()
    }
    // Apertura e chiusura della finestra: l'effetto vale per la sua intera vita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="velo">
      <div ref={finestra} className={`modale${larga ? ' modale-larga' : ''}`} role="dialog" aria-modal="true"
           aria-labelledby={idTitolo} tabIndex={-1}>
        <header className="modale-capo">
          <h2 id={idTitolo}>{titolo}</h2>
          <button type="button" className="bottone bottone-discreto bottone-piccolo"
                  onClick={chiudi} aria-label="Chiudi">Chiudi</button>
        </header>
        <div className="modale-corpo">{children}</div>
        {azioni && <footer className="modale-azioni">{azioni}</footer>}
      </div>
    </div>
  )
}

// --- indicatori --------------------------------------------------------------------------------

export type TonoEtichetta =
  | 'neutro' | 'accento' | 'positivo' | 'attenzione' | 'critico' | 'informativo' | 'riservato'

export function Etichetta({ tono = 'neutro', children }: { tono?: TonoEtichetta; children: ReactNode }) {
  return <span className={`etichetta etichetta-${tono}`}>{children}</span>
}

/**
 * Etichetta con il colore definito nella tassonomia (`ref.area.colore`).
 *
 * Il colore arriva dal dato e non dai token, quindi il contrasto non e' garantito a priori:
 * tre delle cinque aree in uso non raggiungono 4.5:1 con testo bianco. Il colore del testo
 * si calcola sulla luminanza relativa del fondo, come prescrive WCAG 2.1, invece di essere
 * fissato: sopra la soglia si usa il nero istituzionale, sotto il bianco.
 */
export function EtichettaArea({ codice, colore }: { codice: string; colore?: string | null }) {
  const fondo = colore ?? '#6d7780'
  return (
    <span className="etichetta etichetta-area"
          style={{ background: fondo, color: testoLeggibileSu(fondo) }}>
      {codice}
    </span>
  )
}

/** Luminanza relativa secondo WCAG 2.1, formula 1.4.3. */
function luminanza(esadecimale: string): number {
  const h = esadecimale.replace('#', '')
  const pieno = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const canali = [0, 2, 4].map((i) => parseInt(pieno.slice(i, i + 2), 16) / 255)
  const [r, g, b] = canali.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function testoLeggibileSu(fondo: string): string {
  const l = luminanza(fondo)
  // Contrasto con il bianco contro contrasto con il nero: vince il maggiore.
  const conBianco = 1.05 / (l + 0.05)
  const conNero = (l + 0.05) / 0.05
  return conBianco >= conNero ? '#ffffff' : '#181c21'
}

// --- linguette ---------------------------------------------------------------------------------

/**
 * Gruppo di linguette. Esiste perche' `Bottone` non espone `role` ne' `aria-selected`, e senza
 * quegli attributi un gruppo di linguette non e' annunciato come tale: le pagine erano costrette
 * a scrivere `<button role="tab">` grezzi.
 *
 * La navigazione da tastiera segue la pratica ARIA: frecce per spostarsi, Home e Fine agli
 * estremi. Solo la linguetta attiva e' raggiungibile con il tabulatore.
 */
export function Linguette<T extends string>({
  voci, attiva, cambia, etichetta,
}: {
  voci: { valore: T; etichetta: string }[]
  attiva: T
  cambia: (v: T) => void
  /** Descrive il gruppo alle tecnologie assistive: "Sezioni della scheda". */
  etichetta: string
}) {
  const scorri = (e: React.KeyboardEvent, indice: number) => {
    const tasti: Record<string, number> = {
      ArrowRight: indice + 1, ArrowLeft: indice - 1, Home: 0, End: voci.length - 1,
    }
    const prossimo = tasti[e.key]
    if (prossimo === undefined) return
    e.preventDefault()
    cambia(voci[(prossimo + voci.length) % voci.length].valore)
  }
  return (
    <div className="linguette" role="tablist" aria-label={etichetta}>
      {voci.map((v, i) => (
        <button
          key={v.valore} type="button" role="tab"
          aria-selected={attiva === v.valore}
          tabIndex={attiva === v.valore ? 0 : -1}
          onKeyDown={(e) => scorri(e, i)}
          onClick={() => cambia(v.valore)}
        >
          {v.etichetta}
        </button>
      ))}
    </div>
  )
}

// --- paginazione ---------------------------------------------------------------------------------

/**
 * Comandi di paginazione per un elenco.
 *
 * Il totale arriva dall'API come colonna `totale` calcolata con `count(*) over ()`: e' la
 * convenzione gia' usata da /interventi e /audit/eventi, e non richiede una seconda query.
 * Quando il totale non e' noto (rotte che non lo espongono) si passa `totale={null}` e i
 * comandi si regolano sul numero di righe ricevute: avanti resta attivo finche' la pagina e'
 * piena, indietro finche' non si e' all'inizio.
 */
/**
 * Colonna `totale` che le rotte di elenco aggiungono a ogni riga: righe esistenti prima
 * della finestra. E' facoltativa perche' non tutte le rotte la espongono.
 */
export type ConTotale = { totale?: string | number }

/**
 * Legge il totale dalla prima riga di un elenco.
 *
 * `count(*) over ()` e' un bigint e postgres.js lo consegna come stringa: usarla dove ci si
 * aspetta un numero rende falso ogni confronto, e il conteggio del sottotitolo leggeva
 * «1 interventi» con una sola voce. Restituisce null quando la rotta non espone il totale o
 * la pagina e' vuota; null vale come totale ignoto, non come zero.
 */
export function totaleDi(righe: ConTotale[] | null | undefined): number | null {
  const grezzo = righe?.[0]?.totale
  return grezzo === undefined || grezzo === null ? null : Number(grezzo)
}

export function Paginazione({
  salta, limite, totale, ricevute, cambia,
}: {
  salta: number
  limite: number
  /** Righe totali disponibili, oppure null se la rotta non lo espone. */
  totale: number | null
  /** Righe effettivamente ricevute in questa pagina. */
  ricevute: number
  cambia: (salta: number) => void
}) {
  const haPrecedente = salta > 0
  const haSuccessiva = totale === null ? ricevute >= limite : salta + limite < totale
  if (!haPrecedente && !haSuccessiva) return null

  const primo = ricevute === 0 ? 0 : salta + 1
  const ultimo = salta + ricevute

  return (
    <nav className="paginazione" aria-label="Navigazione dell’elenco">
      <span className="paginazione-conteggio" role="status">
        {totale === null
          ? `Righe ${primo}–${ultimo}`
          : `Righe ${primo}–${ultimo} di ${totale}`}
      </span>
      <span className="riga">
        <Bottone variante="secondario" dimensione="piccolo" disabled={!haPrecedente}
                 onClick={() => cambia(Math.max(0, salta - limite))}>
          Precedenti
        </Bottone>
        <Bottone variante="secondario" dimensione="piccolo" disabled={!haSuccessiva}
                 onClick={() => cambia(salta + limite)}>
          Successive
        </Bottone>
      </span>
    </nav>
  )
}

